import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import Fuse from "fuse.js";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../utils/api";
import { cachedList, isFresh, fetchList, prefetchList, driversListPath, countNamed } from "../utils/listCache";
import { track } from "../utils/analytics";
import { callWithWalletGate, WALLET_GATE_CANCELLED } from "../utils/walletGate";
import { getEffectiveAvailability, getAvailabilityAt } from "../utils/availability";
import { detectCarCategory, resolveShortName } from "../utils/carCategory";
import { useCategoryConfig } from "../hooks/useCategoryConfig";
import { CITY_LIST_AP, cityWordsInEnglish } from "../utils/cities";
import { findPlaceNear, supplyCentre, kmBetween, placeWordsOf } from "../utils/placeSearch";
import { logSearch, cancelSearchLog } from "../utils/searchLog";
import { Icon } from "../components/Icon";
import { getUserRole } from "../utils/auth";
import { startAppCall } from "../utils/appCall";
import InstantBookingSheet, { seatClassOf, OneClickBookingBar } from "./InstantBookingSheet";
import { getCompanyConfig } from "../utils/platformConfig";
import EmptyStateBanner from "./EmptyStateBanner";
import { useLanguage } from "../context/LanguageContext";
import { readTripExtras } from "../utils/tripExtras";
import { fillText } from "../utils/leadDisplay";
import { useLocalNames } from "../utils/localNames";
import { carTypeMatches, nativeLanguageNames, fuelLabel, featureBadgeKey } from "../utils/listChoices";
import { askOnBehalf, onBehalfBody, ON_BEHALF_CANCELLED } from "./OnBehalfHost";
import { showBusinessLimit } from "./BusinessLimitHost";
import {
  ResultStrip, ListMessage, Loading, CallConfirmSheet, countWords, openCityPicker, shortPlace,
  PRIMARY_BTN, SECONDARY_BTN,
} from "./home/HomeAtoms";
// Desk staff call drivers themselves; the booking sheet and bar are for customers.
const STAFF_ROLES = ["ADMIN", "SUPER_ADMIN", "ONBOARDING_AGENT"];

/* Rating colour. Red at 1, amber at 3, Sramika green at 5, and interpolated in
   between so a 4.2 and a 4.9 do not wear the same colour — a fixed three-step scale
   made every car from 4.0 up look identical, which is most of the list. Channel-wise
   linear blend between the two nearest stops; it is only three colours, so nothing
   more elaborate earns its keep. */
const RATING_STOPS = [
  { at: 1, rgb: [239, 68, 68] },   // #EF4444
  { at: 3, rgb: [245, 165, 36] },  // #F5A524
  { at: 5, rgb: [123, 174, 0] },   // #7BAE00 - the Sramika green
];

function ratingColor(score) {
  const s = Math.min(5, Math.max(1, Number(score) || 1));
  const hi = RATING_STOPS.findIndex((p) => s <= p.at);
  if (hi <= 0) return `rgb(${RATING_STOPS[0].rgb.join(",")})`;
  const a = RATING_STOPS[hi - 1], b = RATING_STOPS[hi];
  const t = (s - a.at) / (b.at - a.at);
  const mix = a.rgb.map((v, i) => Math.round(v + (b.rgb[i] - v) * t));
  return `rgb(${mix.join(",")})`;
}

/* The card name — "Dzire", "Scorpio", "Innova". An admin can set it per model in the
   Price Controller; resolveShortName falls back to the old heuristic for anything
   nobody has named. Both live in the shared module so the name a customer sees and
   the name an admin configures cannot drift apart. */
const shortName = resolveShortName;

// ── How "Recommended" orders the list ────────────────────────────
/* 1. Available at the trip's pickup time (the owner's switch, then their weekly hours).
   2. Distance band, from the live drive times - applied in displayCars below, once
      those times have arrived.
   3. The server's rankingScore: fewer recent calls, profile score, the partner's own
      saving, and count-weighted rating, blended by the weights in Platform Config ->
      Listing ranking. The weights stay on the server; only the score comes down.
   4. Oldest listing first, as the last tie-break.

   The yes/no "never called" boost that sat here has moved into that score as a
   sliding measure. It switched off after a single call, so a partner with one call
   dropped behind all twenty-one who had none - and below a partner with thirty. */
const AVAILABILITY_TIER = { AVAILABLE: 0, OUTSIDE_HOURS: 1 };
const availabilityTier = (car, when) => AVAILABILITY_TIER[getAvailabilityAt(car, when)] ?? 2;

/* Drive-time bands, in minutes. Inside a band the ranking decides; 14 against 16
   minutes is noise and should not outrank a better partner. A long trip gets wider
   bands - fifteen extra minutes to reach a 300 km round trip changes nothing for the
   customer, and would otherwise hand the top of the list to whoever lives nearest. */
const LOCAL_BANDS = [10, 20, 35];
const LONG_TRIP_BANDS = [30, 60];
const LONG_TRIP_KM = 80;
const distanceBand = (mins, routeKm) => {
  if (!(mins > 0)) return null;
  const bands = Number(routeKm) > LONG_TRIP_KM ? LONG_TRIP_BANDS : LOCAL_BANDS;
  const i = bands.findIndex((limit) => mins <= limit);
  return i === -1 ? bands.length : i;
};

// ── Individual car card ──────────────────────────────────────────

// Cards whose first photo loads at once - the ones on the first screen.
const EAGER_CARDS = 3;

function CarCard({ car, eager = false, onClick, distanceKm, hours, startAt, calculateFare, hasRoute, tripType, cityPricing, matchReason, etaMins, nearLabel, onCallDriver, calling, onSeePrice }) {
  const { t, lang } = useLanguage();
  // The car's and the driver's names in the reader's script (utils/localNames).
  const ln = useLocalNames();

  // Every photo the owner uploaded, de-duplicated and stripped of blanks so the dot
  // count always matches what can actually be swiped to.
  const photos = useMemo(() => {
    const raw = [
      ...(Array.isArray(car.photos) ? car.photos : []),
      car.carImageUrl,
      ...(Array.isArray(car.uploads?.carImages) ? car.uploads.carImages : []),
    ];
    const seen = new Set();
    return raw.filter((u) => typeof u === "string" && u.trim() && !seen.has(u) && seen.add(u));
  }, [car.photos, car.carImageUrl, car.uploads]);

  const [photoIndex, setPhotoIndex] = useState(0);
  const photoStripRef = useRef(null);
  // The other photos load when the strip is first touched, scrolled or hovered. As
  // lazy images they still loaded: Chrome fetches anything within ~2500px, sideways
  // too, so the first 15 cards pulled every photo they had (Oct 2026).
  const [morePhotos, setMorePhotos] = useState(false);
  const wantMorePhotos = () => { if (!morePhotos) setMorePhotos(true); };

  /* One photo per swipe, moved by hand.
     The strip is a native snap scroller and still is — this only decides when to
     scroll it, because with both axes offered to the browser a swipe across a 110px
     photo inside a long vertical list was read as a page scroll on a phone. */
  const photoTouch = useRef(null);
  const onPhotoTouchStart = (e) => {
    wantMorePhotos();
    const t = e.touches[0];
    photoTouch.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const onPhotoTouchEnd = (e) => {
    const el = photoStripRef.current;
    const start = photoTouch.current;
    photoTouch.current = null;
    if (!el || !start || photos.length < 2 || el.clientWidth === 0) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = start.x - t.clientX;
    const dy = Math.abs(start.y - t.clientY);
    // Deliberate and across: 28px of travel, and more of it sideways than down, so a
    // page scroll that happens to begin on a photo is never taken for a swipe.
    if (Math.abs(dx) < 28 || Math.abs(dx) < dy) return;
    const cur = Math.round(el.scrollLeft / el.clientWidth);
    const next = Math.min(photos.length - 1, Math.max(0, cur + (dx > 0 ? 1 : -1)));
    el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" });
  };
  const onPhotoScroll = () => {
    wantMorePhotos();
    const el = photoStripRef.current;
    if (!el || el.clientWidth === 0) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    setPhotoIndex((prev) => (prev === i ? prev : i));
  };

  /* Swiping the photos and tapping the card are the same gesture until the finger
     moves. Previously the strip swallowed every click so photos could be swiped but
     never opened the car, and on a real device a swipe that ended in place still
     fired the card's click and navigated away mid-scroll — which read as "scrolling
     does not work".

     So: measure the movement. Past a few pixels it was a swipe and the click is
     dropped; below that it was a tap and the card opens, photo included. */
  const pointerStart = useRef(null);
  const draggedRef = useRef(false);

  const onPointerDown = (e) => {
    draggedRef.current = false;
    const pt = e.touches?.[0] || e;
    pointerStart.current = { x: pt.clientX, y: pt.clientY };
  };
  const onPointerMove = (e) => {
    if (!pointerStart.current) return;
    const pt = e.touches?.[0] || e;
    const dx = Math.abs(pt.clientX - pointerStart.current.x);
    const dy = Math.abs(pt.clientY - pointerStart.current.y);
    // 8px is comfortably past a resting finger's jitter and well short of a
    // deliberate swipe.
    if (dx > 8 || dy > 8) draggedRef.current = true;
  };
  const handleCardActivate = (e) => {
    if (draggedRef.current) { draggedRef.current = false; return; }
    onClick(e);
  };

  const model       = car.carDetails?.model || car.carModel || car.carType || "Car";
  const year        = car.carDetails?.year  || car.carDetails?.manufacturingYear || car.makeYear || car.manufactureYear || "";
  const fuel        = car.carDetails?.fuelType || car.fuelType || "";
  const seats       = car.carDetails?.seats || car.seatCount || "";
  const driverName  = car.driverName || car.ownerDriver?.name || "";
  const rawDriverPhoto = car.uploads?.driverPhoto || car.driverPhoto || car.driverImageUrl || null;
  const driverPhoto = rawDriverPhoto ? rawDriverPhoto.replace(/#/g, "%23") : null;
  const driverAge   = car.ownerDriver?.age || car.driverAge || car.age || "";

  // The RAW model, not a formatted one. resolveShortName matches the admin's mapping
  // against the full string, and formatCarName has already thrown most of it away —
  // "BMW 3 Series - 320d" reduces to "3", which no mapping would ever match.
  const name = shortName(model);
  const category = detectCarCategory(model, seats);


  // Rating
  const avgRating = car.avgRating ?? car.averageRating ?? car.rating ?? null;
  const ratingCount = car.ratingCount ?? car.reviewCount ?? 0;
  const hasRating = avgRating !== null && avgRating > 0 && ratingCount > 0;
  const starTone = hasRating ? ratingColor(avgRating) : "#7BAE00";

  // Both of these are read by the photo overlays below.
  const availability = getEffectiveAvailability(car);
  const isApproved = String(car.status || car.approvalStatus || "").toUpperCase() === "APPROVED";

  // Pricing
  // The pickup time goes in too: a night halt follows the admin's night hours.
  const fare = (distanceKm > 0 && calculateFare) ? calculateFare(car, hours, distanceKm, tripType, cityPricing, { startAt }) : null;

  /* The market rate for this car's category, and how far below it this partner sits.
     fare.original is the admin's figure; fare.final is what the customer pays. Rounded
     before subtracting so the saving always equals the two numbers shown — subtracting
     first and rounding after can print a difference that is a rupee off the figures
     beside it. */
  const typicalFare = fare ? Math.round(fare.original || 0) : 0;
  /* Shown only when the partner actually set a rate.
     Leave the boxes blank and the engine quotes the suggested share of the market —
     80% today — so a gap appears whether or not anyone chose to undercut anything.
     Eleven of thirty-one cars read exactly "Save 19%" that way: one admin setting
     displayed eleven times, dressed as eleven partners competing. That is the same
     trick as the Rs 1/km markup, and a saving nobody offered is not worth showing. */
  const savingIsPartnerChosen = Boolean(fare?.partnerSetOwnRates);
  const saving = fare && savingIsPartnerChosen
    ? Math.max(0, typicalFare - Math.round(fare.final || 0))
    : 0;
  const savingPct = typicalFare > 0 ? Math.round((saving / typicalFare) * 100) : 0;

  /* The one badge on the photo - the same rule as the car's own page (featureBadgeKey
     in utils/listChoices): top rated, then nearby (this car within five km of the
     pickup - arrival in minutes rather than a wait, and a fraction of the 25km listing
     radius, so it marks the genuinely close few), luxury, family, quick. Short words of
     their own (hm_b*), written to fit a photo in every language. */
  const badgeKey = featureBadgeKey(car, { avgRating, categoryKey: category.key, seats });
  const featureBadge = badgeKey ? t(badgeKey) : null;

  // "Age 41", in the reader's language.
  const driverAgeStr = driverAge ? fillText(t("hm_age"), { n: driverAge }) : "";

  /* How long they have been driving: the band's numbers ("10-15"), then "yrs driving"
     in the reader's language, under the face in the partner column.
     For a 550km outstation trip with a stranger at the wheel — which is what the
     median booking here is — this is the fact a customer actually weighs, and the card
     was showing their age instead. Age alone reads as a risk signal; experience is
     the reassurance it was standing in for. */
  const experienceStr = (() => {
    const raw = String(car.ownerDriver?.drivingExperience || car.drivingExperience || "").trim();
    if (!raw) return "";
    return raw.replace(/\s*years?\s*$/i, "").replace(/\s+/g, " ").trim();
  })();

  /* Names are stored as typed at onboarding, which is usually SHOUTED — "SATISH",
     "KADHAR". Fine in a 64px column of its own; on a button it reads as shouting, and
     "Call SATISH" is not how anyone writes. Title-cased for display only; the record
     is untouched. */
  const firstName = (() => {
    const raw = String(driverName || "").trim().split(/\s+/)[0] || "";
    if (!raw) return "";
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  })();

  // Each language named in the reader's language ("తెలుగు · హిందీ · ఇంగ్లీష్").
  const languagesStr = nativeLanguageNames(car.ownerDriver?.languagesSpoken || car.languagesSpoken || "", lang);

  // What the price covers. Without it, two cars at different totals look arbitrary —
  // the basis was only ever stated on the detail page. The trip's words are the ones
  // on the trip form's toggle.
  const priceBasis = (distanceKm > 0)
    ? `${t(tripType === "ONE_WAY" ? "lp_tabOneWay" : "lp_tabRoundTrip")} · ${fillText(t("hm_km"), { km: Math.round(distanceKm) })}`
    : "";

  return (
    <div
      onClick={handleCardActivate}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      role="button"
      tabIndex={0}
      data-car-card=""
      aria-label={fillText(t("hm_viewCar"), { name })}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(e); }}
      className="bg-white rounded-[16px] overflow-hidden p-[6px] sm:p-0 flex flex-col h-full cursor-pointer transition-all duration-200 ease-out border border-[#E2E8F0] shadow-[0_2px_12px_rgba(15,23,42,0.06)] active:scale-[0.99] md:hover:-translate-y-[1px] md:hover:shadow-[0_6px_24px_rgba(0,0,0,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7BAE00] focus-visible:ring-offset-2"
    >
      {/* Top row: the car and what it is. Everything that follows - who is driving,
          the price, the call — runs the FULL width of the card underneath, because
          those are the lines a customer reads across, not down a narrow column beside
          a photo. The driver takes the corner the headline was not using, which costs
          no height at all — it was empty space. */}
      <div className="flex flex-row items-stretch gap-[6px] pb-[7px] sm:flex-col sm:gap-0 sm:pb-0">

      {/* Photo - a 4:3 column beside the text on a phone, a 16:9 banner once the grid
          goes multi-column.
          It was 32% wide and stretched to the text's height, so the box came out nearly
          square and a landscape car photo was centre-cropped hard — the car was the
          thing being sold and the thing being cut. It holds a true 4:3 now, which it
          could not while the price and the call were stacked beside it: the column of
          text was always taller, so either the photo stretched and cropped or it kept
          its shape and left a band of white down the card's edge. With that content
          moved below, the row is only as tall as the photo and the shape is free.
          Swipes horizontally through every photo the owner uploaded. */}
      <div className="relative bg-[#F6F8FC] w-[34%] max-w-[180px] aspect-[10/7] rounded-[8px] sm:w-full sm:max-w-none sm:aspect-[16/9] sm:rounded-none overflow-hidden shrink-0">
        {photos.length > 0 ? (
          <>
            {/* The skeleton sits BEHIND the photo and is simply covered once there are
                pixels to cover it. It used to be an either/or driven by an onLoad
                flag, and a cached image finishes loading before React can attach that
                handler — so the event never fired, the flag stayed false, and the
                photo rendered at opacity 0. Every returning visitor with a warm cache
                saw blank cards. Nothing here can now leave a loaded image invisible. */}
            <div className="absolute inset-0 z-0 bg-[#E2E8F0] overflow-hidden" aria-hidden="true">
              <div className="w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[shimmer_1.5s_infinite] -translate-x-full" style={{ backgroundSize: '200% 100%' }} />
            </div>

            {/* Scroll-snap rather than a JS carousel: native momentum, no state to
                desync, and it degrades to a plain scroller if anything fails. The
                click guard stops a swipe that ends on the strip from also opening
                the car. */}
            <div
              ref={photoStripRef}
              onScroll={onPhotoScroll}
              onTouchStart={onPhotoTouchStart}
              onTouchEnd={onPhotoTouchEnd}
              onMouseEnter={wantMorePhotos}
              // No click handling here: the card decides, using the drag test above,
              // so a tap on a photo opens the car like a tap anywhere else.
              // touch-action pan-y, and the horizontal swipe is ours. It used to be
              // "pan-x pan-y", which let the browser own both axes and decide between
              // them from the first few pixels of movement — and on a phone this strip
              // is a third of a card in a list 31 cards long, so the vertical page
              // scroll won that contest almost every time. It read as photos that
              // simply would not swipe, while a trackpad's unambiguous horizontal
              // scroll worked on a laptop. Vertical still pans the page from here.
              className="absolute inset-0 z-10 flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
              style={{ scrollbarWidth: "none", touchAction: "pan-y" }}
            >
              {photos.map((src, i) => (i > 0 && !morePhotos ? (
                // A blank of the same size keeps the strip scrollable and the count right.
                <span key={src + i} aria-hidden="true" className="w-full h-full shrink-0 snap-center bg-[#E2E8F0]" />
              ) : (
                <img
                  key={src + i}
                  src={src}
                  alt={i === 0 ? name : fillText(t("hm_photoOf"), { n: i + 1, name })}
                  // Only the first cards' first photo loads at once; the rest wait until
                  // they are scrolled near. Eager on every card fetched ~30 photos (7 MB)
                  // before the first one showed on a phone (Oct 2026).
                  loading={eager && i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  draggable={false}
                  className="w-full h-full shrink-0 snap-center object-cover object-center"
                />
              )))}
            </div>

            {/* A counter rather than dots: "1 / 8" says up front how many photos there
                are to swipe through, where eight dots at 3px said only that there were
                some. Bottom-right, so the On Trip / Maintenance pill keeps bottom-left.
                Only when there is more than one - a lone photo is not given a count. */}
            {photos.length > 1 && (
              <span className="absolute bottom-[6px] right-[6px] z-20 pointer-events-none rounded-full bg-[rgba(15,23,42,0.62)] px-[5px] py-[1px] text-[8.5px] font-bold text-white leading-[13px] tabular-nums">
                {photoIndex + 1} / {photos.length}
              </span>
            )}
          </>
        ) : (
          <svg viewBox="0 0 120 55" className="w-20 h-auto opacity-40 z-10" fill="none" aria-hidden="true">
            <rect x="4" y="28" width="112" height="20" rx="5" fill="#CBD5E1"/>
            <path d="M18 28 L34 13 L86 13 L102 28Z" fill="#94A3B8"/>
            <circle cx="27" cy="48" r="7" fill="#64748B"/><circle cx="27" cy="48" r="3.5" fill="#CBD5E1"/>
            <circle cx="93" cy="48" r="7" fill="#64748B"/><circle cx="93" cy="48" r="3.5" fill="#CBD5E1"/>
          </svg>
        )}

        {/* ONE badge, top-left on the photo. There used to be two - a rating-or-New
            Partner chip here and the feature badge on the name's line — which put two
            green pills on every card and pushed the name onto a second line whenever
            it was longer than "Dzire". They say the same kind of thing, so they now
            share a slot in priority order: a real rating beats a claim, and a claim
            beats the fallback. The name gets its line back. */}
        <div className="absolute top-[5px] left-[5px] right-[5px] z-20 flex items-start pointer-events-none">
          {hasRating ? (
            <span className="shrink-0 whitespace-nowrap inline-flex items-center gap-[2px] rounded-full bg-white px-[6px] py-[2px] text-[9.5px] font-bold shadow-[0_1px_3px_rgba(15,23,42,0.22)]" style={{ color: starTone }}>
              <Icon name="star" size={9} fill={starTone} stroke={starTone} />
              {avgRating.toFixed(1)}
              <span className="text-[9px] font-medium text-[#64748B]">({ratingCount})</span>
            </span>
          ) : (
            // A blank where a rating belongs reads as "poor". The feature badge is a
            // truer thing to say than "New Partner", so it goes first; New Partner is
            // what is left when there is nothing else, and matches the wording the
            // hire driver list uses.
            // Never cut: a longer word in Tamil or Telugu wraps to a second line inside
            // the photo rather than ending in "…".
            <span className="max-w-full inline-flex items-center rounded-[10px] bg-white px-[7px] py-[2px] text-[9px] font-bold text-[#5E8700] leading-tight shadow-[0_1px_3px_rgba(15,23,42,0.22)]">
              {featureBadge || t("hm_bNew")}
            </span>
          )}
        </div>

        {/* No "Available" tag. Nearly every car in the list is available, so the pill
            said nothing on the cards that carried it and only took up the corner. The
            exceptions still do say something, so On Trip and Maintenance keep the
            bottom-left spot — a card without a pill now simply means available. */}
        {(availability === "ON_TRIP" || availability === "MAINTENANCE") && (
          <div className="absolute bottom-1.5 left-1.5 z-20">
            <span className={`inline-flex items-center gap-[3px] rounded-full backdrop-blur-sm px-[7px] py-[2px] text-[10px] font-bold text-white shadow-sm ${
              availability === "ON_TRIP" ? "bg-blue-600/90" : "bg-amber-500/90"
            }`}>
              <span className={`w-[5px] h-[5px] rounded-full bg-white ${availability === "ON_TRIP" ? "animate-pulse" : ""}`} />
              {availability === "ON_TRIP" ? t("hm_onTrip") : t("hm_repair")}
            </span>
          </div>
        )}
      </div>

      {/* The headline - name, specs and arrival, between the car and the driver. */}
      <div className="flex-1 min-w-0 self-center py-[1px] sm:self-auto sm:px-[12px] sm:pt-[10px] sm:pb-0">

          {/* Name, alone on its line now that the feature badge has moved onto the
              photo. It used to share the line and wrap, which is how one card stood
              13px taller than the next for no reason a customer could see. */}
          <h3 className="text-[13px] font-bold text-[#1F2937] leading-tight truncate">
            {ln(name, "car")}
          </h3>
          {/* A car from the drop's city, driving home with the customer (mock Seo_BF change 1). */}
          {car.homeReturn && (
            <span className="inline-block mt-[3px] rounded-[6px] bg-[#E4F4D4] px-[6px] py-[1px] text-[9.5px] font-extrabold text-[#3D7A12] leading-tight">
              {fillText(t("hm_homeReturn"), { city: ln(car.city || "", "place") })}
            </span>
          )}

          {/* Seats, year and fuel, each with its own glyph. They were one run of text
              joined by bullets, which reads as a single vague label; as three marked
              facts the eye picks out the one it came for. */}
          <div className="flex items-center gap-x-[6px] gap-y-[3px] flex-wrap mt-[5px] text-[9.5px] font-semibold text-[#334155] leading-tight">
            {seats > 0 && (
              <span className="inline-flex items-center gap-[3px] whitespace-nowrap">
                <Icon name="users" size={10} stroke="#7BAE00" strokeWidth={2} />{fillText(t("hm_nSeats"), { n: seats })}
              </span>
            )}
            {year && (
              <span className="inline-flex items-center gap-[3px] whitespace-nowrap">
                <Icon name="calendar" size={10} stroke="#7BAE00" strokeWidth={2} />{year}
              </span>
            )}
            {fuel && (
              <span className="inline-flex items-center gap-[3px] whitespace-nowrap">
                <Icon name="fuel" size={10} stroke="#7BAE00" strokeWidth={2} />{fuelLabel(fuel, t)}
              </span>
            )}
          </div>

          {/* "Sramika Recommended" (1 Nov 2026, mock Rec_1): earned by the driver's own record. */}
          {Date.parse(car.recommendedUntil || 0) > Date.now() && (
            <p className="mt-[5px]"><span className="inline-flex items-center gap-[3px] rounded-full bg-[#EAF5D3] px-1.5 py-[1px] text-[9px] font-extrabold text-[#3F5E00]">✓ {t("rc_badge")}</span></p>
          )}

          {/* How far away the car is, from the owner's address to this pickup. Only ever
              a real Distance Matrix answer — hidden when it could not be computed,
              never the old city-centre ring estimate. */}
          {etaMins > 0 && (
            <p className="mt-[5px] text-[9.5px] font-bold text-[#5E8700] leading-tight flex items-center gap-[3px]">
              {/* A pin, not a clock: this is how far the car is from the pickup,
                  said in minutes. The clock read as a wait or a trip duration. */}
              <Icon name="mapPin" size={10} strokeWidth={2} />
              {fillText(t("hm_minAway"), { n: etaMins })}
            </p>
          )}

          {/* When the search was a place: how far this car is from it. Straight-line,
              and said as "about", because that is what it is. */}
          {nearLabel && (
            <p className="mt-[5px] text-[9.5px] font-bold text-[#5E8700] leading-tight flex items-center gap-[3px]">
              <Icon name="mapPin" size={10} strokeWidth={2} />
              {nearLabel}
            </p>
          )}

          {/* Languages, here rather than by the price. It belongs with who and what you
              are hiring, and this column had two lines of text against a photo three
              times as tall — the gap was the whole of the wasted space on the card.
              Each in its own script, so a reader finds theirs without reading English. */}
          {languagesStr && (
            <p className="flex items-start gap-[3px] text-[9px] font-medium text-[#64748B] leading-snug mt-[5px]">
              <Icon name="globe" size={10} stroke="#7BAE00" strokeWidth={2} className="shrink-0 mt-[2px]" />
              {/* Two lines at most, never an ellipsis: the last language was the one
                  being cut, and it is the one a customer is checking for. */}
              <span className="min-w-0 line-clamp-2">{languagesStr}</span>
            </p>
          )}

          {matchReason && (
            <p className="text-[9px] font-bold text-[#5E8700] leading-tight mt-[5px] truncate">
              {fillText(t("hm_matchedBy"), { what: matchReason })}
            </p>
          )}
      </div>

      {/* The driver, in the corner the headline was not using.
          A full-width row of his own cost a line and left this space empty. Here the
          face sits where the eye already is, and the two facts that matter hang under
          it: how long they have been driving, and how old they are. Narrow on purpose
          — it holds a portrait and two short labels, never a button, which is what
          made this column shout the last time it existed.
          A rounded square, not a circle: it sits beside a rounded-square car photo, and
          a circle beside a tile reads as two different kinds of thing. */}
      <div className="w-[70px] shrink-0 self-center flex flex-col items-center sm:self-auto sm:w-full sm:flex-row sm:justify-center sm:gap-2 sm:pt-[6px]">
        <span className="relative block w-[42px] h-[42px]" aria-hidden="true">
          <span className="w-full h-full rounded-[6px] overflow-hidden ring-1 ring-[#E2E8F0] bg-[#7BAE00] flex items-center justify-center">
            {driverPhoto ? (
              // Centre crop. object-top was tried and is worse for these photos: they
              // are phone portraits with sky or wall above the subject, so anchoring
              // to the top framed the background and cut the face off entirely.
              <img src={driverPhoto} alt="" className="w-full h-full object-cover object-center" loading="lazy" decoding="async" />
            ) : (
              <span className="text-[13px] font-bold text-white">
                {(ln(firstName, "person") || "D").slice(0, 1).toUpperCase()}
              </span>
            )}
          </span>
          {/* Verified tick, at the top-right corner of the tile. Driven by the same
              APPROVED status the list is filtered on, so it is a fact about the partner
              rather than decoration — it will simply not render for anything that ever
              reaches this grid unapproved. */}
          {isApproved && (
            <span className="absolute -top-[3px] -right-[3px] w-[15px] h-[15px] rounded-full bg-[#7BAE00] ring-[1.5px] ring-white flex items-center justify-center">
              <Icon name="check" size={8} stroke="#FFFFFF" strokeWidth={4} />
            </span>
          )}
        </span>
        {/* Whole words, never "…": the age, then the years of driving - the number in
            green - wrapping onto a second line when a language needs it. */}
        {driverAgeStr && (
          <span className="mt-[4px] sm:mt-0 w-full sm:w-auto text-center text-[9px] font-medium text-[#64748B] leading-tight">
            {driverAgeStr}
          </span>
        )}
        {experienceStr && (
          <span className="mt-[2px] sm:mt-0 w-full sm:w-auto text-center text-[9px] font-medium text-[#64748B] leading-tight">
            <b className="font-bold text-[#5E8700] whitespace-nowrap">{fillText(t("hm_yrs"), { yrs: experienceStr })}</b> {t("hm_driving")}
          </span>
        )}
      </div>
      </div>

      {/* One line under the photo, on its own hairline: what the price covers, the
          price, and the way to act on it.
          These were three stacked rows — a driver line, a price row, then a full-width
          button — which is three lines of card height for one decision. Side by side
          they read as a single sentence, and the card loses roughly a third of its
          height without losing a word. */}
      <div className="mt-auto pt-[7px] border-t border-[#EFF3F7] sm:mx-[12px] sm:mb-[11px] flex items-center gap-[8px]">

        {/* Left: what the number is for. Empty before a route is set, on purpose -
            there is no price to caption then, and the customer-facing facts have all
            moved up beside the car where the space was going unused. */}
        <div className="flex-1 min-w-0">
          {fare && hasRoute ? (
            saving > 0 ? (
              /* The saving takes the slot rather than sitting under the trip caption.
                 Two stacked greys and greens where one line will do is what made this
                 corner look busy, and the struck figure beside the price already says
                 what the trip is being compared against. */
              /* A pill, so the saving reads as its own fact rather than a caption.
                 max-w-full with truncate: on a 360px phone with a five-figure fare it
                 gives way from the end, so the percentage goes before the rupees do. */
              /* Whole, never cut: where a language's word for "save" is long, the
                 percentage goes to a second line inside the pill. */
              <span className="inline-block max-w-full rounded-[9px] bg-[#EEF6DC] px-[9px] py-[6px] text-[9.5px] font-bold text-[#4F7A00] leading-[1.2]">
                {fillText(t("hm_saveAmt"), { amt: saving.toLocaleString('en-IN') })}
                {savingPct > 0 && <span className="whitespace-nowrap"> ({savingPct}%)</span>}
              </span>
            ) : (
              <p className="text-[9.5px] font-medium text-[#64748B] leading-tight truncate">{priceBasis}</p>
            )
          ) : (
            /* Before a route there is no price. "See price ›" says what to do about
               it and does it: one tap opens the trip form, the one step that puts a
               price on every card. It was a grey "Set route for price" that did
               nothing, on a card whose every other tap went to the same form. */
            onSeePrice ? (
              <button type="button"
                onClick={(e) => { e.stopPropagation(); onSeePrice(); }}
                className="min-h-[44px] -my-[6px] pr-2 inline-flex items-center gap-[2px] text-[11px] font-extrabold text-[#5E8700] active:opacity-70">
                {t("hm_seePrice")}
                <Icon name="chevronRight" size={14} strokeWidth={2.6} />
              </button>
            ) : null
          )}
        </div>

        {/* The price, the loudest thing on the card, because it is what the customer is
            deciding on. The market figure sits beside it struck through rather than
            above it — on one line there is no above. */}
        {fare && hasRoute && (
          <div className="shrink-0 flex items-baseline gap-[5px]">
            {saving > 0 && (
              <span className="text-[10px] font-medium text-[#94A3B8] line-through">
                ₹{typicalFare.toLocaleString('en-IN')}
              </span>
            )}
            <span className="text-[16.5px] font-extrabold text-[#0F172A] tracking-tight leading-none" aria-label={`₹${Math.round(fare.final)}`}>
              ₹{Math.round(fare.final).toLocaleString('en-IN')}
            </span>
          </div>
        )}


        {/* Call - short, filled, and last. The action after the decision, and the only
            solid block on the card, so there is never a question of what to press.
            The label is the verb alone: the driver's name on it made the button the
            widest thing on the line and squeezed the price caption on a 360px phone,
            and the name is still read out by the accessible label below.
            Always offered; with no route it sends the customer to the picker to set
            one rather than being invisible and leaving them to wonder how to call.
            stopPropagation so it never doubles as a card tap. */}
        {onCallDriver && (
          <button
            type="button"
            disabled={calling}
            onClick={(e) => { e.stopPropagation(); onCallDriver(car); }}
            aria-label={firstName ? fillText(t("hs_callName"), { name: ln(firstName, "person") }) : t("hm_call")}
            className="shrink-0 min-w-[88px] min-h-[44px] inline-flex items-center justify-center gap-[6px] rounded-[11px] bg-[#7BAE00] px-3 text-[11.5px] font-bold text-white shadow-[0_2px_6px_rgba(123,174,0,0.35)] active:scale-[0.98] transition-transform disabled:opacity-60 md:hover:bg-[#6E9D00]"
          >
            <Icon name="phone" size={14} stroke="#FFFFFF" fill="#FFFFFF" strokeWidth={1.4} />
            {calling ? "…" : t("hm_call")}
          </button>
        )}
      </div>
    </div>
  );
}

export function matchesDriverAge(car, filter) {
  if (!filter) return true;
  const options = filter.split(",");
  return options.some(opt => {
    const age = Number(car.ownerDriver?.age || car.driverAge || 0);
    if (!age) return false;
    /* Normalise en dash and em dash to a plain hyphen before splitting the range.
       The class must keep the long dashes even though the rest of the app now writes
       plain hyphens: an age filter stored earlier, or pasted from anywhere else, can
       still carry them, and [--] matches only a hyphen — which would make this a
       no-op and send "25–40" down the 60+ branch. */
    const normalizedFilter = opt.replace(/[–—]/g, "-");
    if (normalizedFilter === "60+") {
      return age > 60;
    }
    const parts = normalizedFilter.split("-");
    if (parts.length === 2) {
      const min = Number(parts[0]);
      const max = Number(parts[1]);
      return age >= min && age <= max;
    }
    return false;
  });
}

/* By the car's price category, grouped into the types a customer knows - see
   carTypeMatches in utils/listChoices for why the key and not the name on screen. */
export function matchesCarType(car, filter) {
  if (!filter) return true;
  const options = filter.split(",");
  const model = (car.carDetails?.model || car.carModel || car.carType || "").toLowerCase();
  const seats = String(car.carDetails?.seats || car.seatCount || car.seats || "");
  const cat = detectCarCategory(model, seats);
  return options.some((opt) => carTypeMatches(opt, cat));
}

export function matchesSeats(car, filter) {
  if (!filter) return true;
  const options = filter.split(",");
  return options.some(opt => {
    const seats = String(car.carDetails?.seats || car.seatCount || car.seats || "");
    const filterNum = opt.replace(/[^0-9]/g, "");
    return seats === filterNum;
  });
}

export function matchesYear(car, filter) {
  if (!filter) return true;
  const options = filter.split(",");
  return options.some(rawOpt => {
    const year = Number(car.carDetails?.year || car.carDetails?.manufacturingYear || car.makeYear || car.manufactureYear || 0);
    if (!year) return false;
    /* These labels used to be written with an en dash and are held in sessionStorage,
       so a session that started before the app switched to plain hyphens still has
       "2020–23" saved. Matching that against "2020-23" fails silently and the year
       filter returns no cars at all, which reads as a broken page rather than a
       stale value. Normalised the same way the driver-age filter is. */
    const opt = rawOpt.replace(/[–—]/g, "-");
    switch (opt) {
      case "2024+":   return year >= 2024;
      case "2020-23": return year >= 2020 && year <= 2023;
      case "2016-19": return year >= 2016 && year <= 2019;
      case "≤2015":   return year <= 2015;
      default: return false;
    }
  });
}

export function matchesFuelType(car, filter) {
  if (!filter) return true;
  const options = filter.split(",");
  return options.some(opt => {
    const fuel = (car.carDetails?.fuelType || car.fuelType || "").toLowerCase();
    const fOpt = opt.toLowerCase();
    if (fOpt === "ev" || fOpt === "electric") {
      return fuel.includes("electric") || fuel.includes("ev");
    }
    return fuel.includes(fOpt);
  });
}

export function matchesExperience(car, filter) {
  if (!filter) return true;
  const options = filter.split(",");
  return options.some(opt => {
    const rawExp = car.ownerDriver?.drivingExperience || car.drivingExperience || "";
    if (!rawExp) return false;
    
    // Extract the first number sequence from the experience string (e.g. "3-5 years" -> 3)
    const match = String(rawExp).match(/^(\d+)/);
    const exp = match ? Number(match[1]) : 0;
    if (!exp) return false;

    switch (opt) {
      case "3+ Years": return exp >= 3;
      case "5+ Years": return exp >= 5;
      case "10+ Years": return exp >= 10;
      default: return false;
    }
  });
}

export function matchesRating(car, filter) {
  if (!filter) return true;
  const options = filter.split(",");
  return options.some(opt => {
    const ownerDriver = car.ownerDriver || {};
    const avg = Number(car.avgRating ?? car.averageRating ?? car.rating ?? ownerDriver.avgRating ?? 0);
    if (!avg) return false;
    switch (opt) {
      case "3+ Stars": return avg >= 3.0;
      case "4+ Stars": return avg >= 4.0;
      case "5 Stars":  return avg === 5.0;
      default: return false;
    }
  });
}

export function matchesDriverTags(car, filter) {
  if (!filter) return true;
  const options = filter.split(",");
  const keyMap = {
    "On Time": ["punctuality", "punctual"],
    "Well Behaved": ["driverBehaviour", "behaviour", "polite"],
    "Comfortable": ["rideComfort", "comfort"],
    "Cleanliness": ["cleanliness", "clean"],
    "Safe Driver": ["safety", "safe"]
  };

  const ownerDriver = car.ownerDriver || {};
  const avgCriteria = car.avgCriteria || ownerDriver.avgCriteria || null;
  const ratings = car.ratings || ownerDriver.ratings || [];

  // Drivers must satisfy ALL selected tags (AND condition)
  return options.every(opt => {
    const keys = keyMap[opt];
    if (!keys) return true;

    // 1. Direct match on aggregated criteria scores (e.g. cleanliness, safety)
    if (avgCriteria && typeof avgCriteria === "object") {
      let sum = 0;
      let count = 0;
      keys.forEach(k => {
        if (typeof avgCriteria[k] === "number" && avgCriteria[k] > 0) {
          sum += avgCriteria[k];
          count++;
        }
      });
      if (count > 0) {
        return (sum / count) >= 4.0;
      }
    }

    // 2. Fallback to mapping reviews array
    if (ratings.length > 0) {
      let sum = 0;
      let count = 0;
      ratings.forEach(r => {
        const c = r.criteria || {};
        keys.forEach(k => {
          if (typeof c[k] === "number" && c[k] > 0) {
            sum += c[k];
            count++;
          }
        });
      });
      if (count > 0) {
        return (sum / count) >= 4.0;
      }
    }

    // 3. Last fallback to overall average rating
    const avg = Number(car.avgRating ?? car.averageRating ?? car.rating ?? ownerDriver.avgRating ?? 0);
    return avg >= 4.0;
  });
}

export function matchesBudgetRange(car, filter, fareContext = {}) {
  if (!filter || filter === "Any" || filter === "") return true;

  const { hours, distanceKm, tripType, cityPricing, calculateFare, startAt } = fareContext;

  const fareObj = (distanceKm > 0 && calculateFare) 
    ? calculateFare(car, hours, distanceKm, tripType, cityPricing, { startAt }) 
    : null;

  if (!fareObj || typeof fareObj.final !== "number") return false;

  const fare = fareObj.final;

  if (filter.startsWith("CUSTOM:")) {
    const [minStr, maxStr] = filter.replace("CUSTOM:", "").split("-");
    const min = Number(minStr) || 0;
    const max = Number(maxStr) || Infinity;
    return fare >= min && fare <= max;
  }

  const filters = filter.split(",");
  return filters.some(raw => {
    // Same stored-label problem as the year filter above.
    const f = raw.replace(/[–—]/g, "-");
    if (f.includes("Under ₹5k")) return fare < 5000;
    if (f.includes("₹5k-₹10k")) return fare >= 5000 && fare < 10000;
    if (f.includes("₹10k-₹15k")) return fare >= 10000 && fare < 15000;
    if (f.includes("Above ₹15k")) return fare >= 15000;
    return false;
  });
}

export function matchesMusicPreference(car, filter) {
  if (!filter || filter === "Any") return true;
  const music = car.musicPreference || "Bluetooth / Aux";
  return music.toLowerCase().includes(filter.toLowerCase().split(" ")[0]);
}

export function matchesLanguage(car, filter) {
  if (!filter || filter === "Any") return true;
  const selectedLangs = filter.split(",").map(l => l.trim().toLowerCase());
  
  // The driver's languages live on ownerDriver, as the card and the search read them;
  // reading only the car's own field put every car on the "Telugu, English, Hindi"
  // guess below, so Tamil found no one and Telugu found everyone.
  const carData = car.ownerDriver?.languagesSpoken ?? car.languagesSpoken ?? car.languages ?? car.driverLanguages;
  const carLangStr = (typeof carData === "string") ? carData : 
                     (Array.isArray(carData) ? carData.join(",") : "Telugu, English, Hindi");
                     
  const carLangs = carLangStr.split(",").map(l => l.trim().toLowerCase()).filter(Boolean);
  
  return selectedLangs.some(selected => carLangs.includes(selected));
}

/* ── What the search words are about ─────────────────────────────────────────
   The no-results screens and the place lookup read the same lists, so a word
   that opens the login prompt is never also sent to the map. */
const SUPPORT_KEYS = ["customer", "care", "support", "contact", "help", "call", "complain", "complaint", "issue", "problem", "helpdesk"];
const INTENT_KEYS = {
  account: ["login", "signin", "account", "profile"],
  partner: ["register", "partner", "join", "attach", "onboard", "earn"],
  trips:   ["booking", "bookings", "history", "trips"],
  price:   ["price", "fare", "cost", "rate", "calculator", "estimate"],
  route:   ["airport", "station", "railway", "drop", "pickup"],
};
// App words: never a place. "route" words are left out on purpose - "railway
// station" is exactly the kind of place a customer wants cars near.
const APP_WORDS = [...SUPPORT_KEYS, ...INTENT_KEYS.account, ...INTENT_KEYS.partner, ...INTENT_KEYS.trips, ...INTENT_KEYS.price];

/* Fuse scores run 0 (exact) to 1. Above this the "match" is a coincidence of letters -
   "kanuru" brushing a driver called Karuna - and a place reading is worth asking for. */
const WEAK_TEXT_SCORE = 0.3;

const kmText = (km, t) => (km < 1 ? t("hm_under1km") : fillText(t("hm_aboutKm"), { km: km < 10 ? km.toFixed(1) : Math.round(km) }));

/* Why a car matched a search, in the reader's words. */
const MATCH_WORDS = {
  carModel: "hm_mModel", carBrand: "hm_mBrand", driverName: "hm_mDriver",
  categoryLabel: "hm_carType", category: "hm_carType", ratingTags: "hm_rating",
  tripTypes: "hm_tripType", languages: "hm_mLang", seaterCount: "hm_seats", fuelType: "hm_fuel",
};

// ── Main CarGrid ───────────────────────────────────────────────────────────
export default function CarGrid({
  tripType,
  hours,
  distanceKm,
  fromLocation,
  toLocation,
  fromDateTime,
  returnDateTime,
  // The places on the way, so a car's page measures the same trip (Oct 2026).
  stops,
  calculateFare,
  cityPricing,
  cityFilter,
  searchQuery,
  onClearFilters,
  // A page's own narrowing (mock Seo_12: one model, "Innova on rent") - (car) => boolean.
  carFilter,
  filterDriverAge,
  filterCarType,
  filterSeats,
  filterYear,
  filterFuelType,
  filterExperience,
  filterRating,
  filterDriverTags,
  filterBudgetRange,
  filterMusicPreference,
  filterLanguage,
  sortBy,
  onCarsLoaded,
  onNoCars,
  // "Share list" on the result line: the list, as a link (Home only).
  onShare,
  // Home (mock Home_1): a card to show under the third car on a phone (the visitor's login card).
  afterThird,
  showPriceBreakup = false,
  bookingEnabled = false,
  onLoginRequired,
  onUsePlaceAsPickup,
  onUsePlaceAsDrop,
  // Opens the trip form: "See price", a card or Call tapped before the trip is set.
  onSetRoute,
  // The chosen sort, in the reader's words, for the result line.
  sortLabel,
  // No "minutes away": a season trip page starts from the city, not a pickup (Oct 2026).
  noEta = false,
  // An owner's own link (/o/<CODE>): only their cars (Oct 2026).
  shareCode = null
}) {
  // Redraw when an admin changes a category or short-name rule.
  useCategoryConfig();

  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  // Cities, areas and names in the reader's script; the values behind them stay English.
  const ln = useLocalNames();
  const [cars,    setCars]    = useState([]);
  // Loading until the first answer: an empty list before it said "no cars in the city"
  // for a moment on every Cars ⇄ Drivers switch (26 Oct 2026).
  const [loading, setLoading] = useState(true);
  const notifiedCityRef = useRef("");

  const city = cityFilter ||
    (typeof fromLocation === "string" ? fromLocation.split(",")[0]?.trim() : "") ||
    (fromLocation?.value ? fromLocation.value.split(",")[0]?.trim() : "");

  // Local pickup name for the "cars available" banner (e.g. "Gollapudi") — the first
  // segment of the chosen pickup, falling back to the filter city. Cars are still listed
  // by city; only the label shows the local area, mirroring the hire driver list.
  const carAreaLabel =
    (typeof fromLocation === "string" ? fromLocation : fromLocation?.value || "").split(",")[0].trim() || city;

  /* Supply is chosen by distance from the pickup to where each car actually is, not by
     the city name the pickup resolved to.
     The city string here is the first segment of the pickup text, so for a pickup in
     Gannavaram it IS "Gannavaram" — a name no owner is registered under. That used to
     empty the result outright. It is still sent, because the backend needs it for the
     rare car that has no coordinates yet, but it no longer selects the pool.
     Without a pickup set there is nothing to measure from, and it stays a city query. */
  const pickupPoint =
    (typeof fromLocation === "object" && fromLocation?.location?.lat != null && fromLocation?.location?.lng != null)
      ? { lat: fromLocation.location.lat, lng: fromLocation.location.lng }
      : null;
  // A one-way trip's drop: cars from there come out and drive home with the customer (mock Seo_BF).
  const dropPoint = (tripType === "ONE_WAY" && typeof toLocation === "object" && toLocation?.location?.lat != null && toLocation?.location?.lng != null)
    ? { lat: toLocation.location.lat, lng: toLocation.location.lng }
    : null;
  const pickupKeyForFetch = pickupPoint ? `${pickupPoint.lat},${pickupPoint.lng}${dropPoint ? `>${dropPoint.lat},${dropPoint.lng}` : ""}` : "";

  // Same test the cards use, hoisted so the list banner can ask it too. Declared
  // inside the card map it was invisible to everything above the list.
  const routeIsSet = !!((fromLocation?.value || fromLocation) && (toLocation?.value || toLocation));

  /* One request per settled search, not one per dependency change.
   *
   * The city resolves from the pickup's text before the pickup's COORDINATES arrive —
   * restored from session storage a tick later — so this ran twice on every load and
   * threw the first answer away. At 380 kB a response that is most of a megabyte to
   * show one list, on phones that pay for it by the megabyte.
   *
   * A short debounce collapses that burst, and `alive` makes a superseded response
   * unable to land: promise completion order is not guaranteed, so without it the
   * city-only list could arrive after the radius-filtered one and quietly replace it
   * with cars that are too far away. */
  useEffect(() => {
    if (!city) { setCars([]); setLoading(false); return; }
    let alive = true;

    const geo = pickupPoint
      // No radiusKm: the server applies the city's admin-set listing radius. Sending a
      // figure from here would let a stale bundle override a setting the admin changed.
      ? `&lat=${encodeURIComponent(pickupPoint.lat)}&lng=${encodeURIComponent(pickupPoint.lng)}`
        + (dropPoint ? `&dlat=${encodeURIComponent(dropPoint.lat)}&dlng=${encodeURIComponent(dropPoint.lng)}` : "")
      : "";
    const only = shareCode ? `&share=${encodeURIComponent(shareCode)}` : "";
    const path = `/cars/visible?city=${encodeURIComponent(city)}&status=APPROVED${geo}${only}`;

    const show = (res) => {
      const list = (res?.data || []).filter(
        (c) => String(c.status || c.approvalStatus || "").toUpperCase() === "APPROVED"
      );
      setCars(list);
      if (onCarsLoaded) onCarsLoaded(list);
      if (list.length === 0 && onNoCars && notifiedCityRef.current !== city) {
        notifiedCityRef.current = city;
        onNoCars(city);
      }
    };

    /* A list seen in the last 45 minutes shows at once - no spinner on Cars ⇄ Drivers -
       and is asked for again quietly when it is more than a minute old (utils/listCache). */
    const kept = cachedList(path);
    if (kept) { show(kept); setLoading(false); } else setLoading(true);
    if (kept && isFresh(path)) return () => { alive = false; };

    const timer = setTimeout(() => {
      fetchList(path)
        .then((res) => {
          if (!alive) return;
          show(res);
          // The Drivers tab's list, ready before it is opened.
          if (!shareCode) prefetchList(driversListPath(city));
        })
        .catch(() => { if (alive && !kept) setCars([]); })
        .finally(() => { if (alive) setLoading(false); });
    }, 200);

    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, pickupKeyForFetch, shareCode]);

  // ── Search + Filter ────────────────────────────────────────────────────
  // Dots and commas are spaces: a driver's initials typed "sk.name" found nobody, though
  // the driver "Sk Name" was listed (a customer's search, 10 Oct 2026).
  let normalizedSearch = searchQuery.toLowerCase().replace(/[.,]+/g, " ").replace(/\s+/g, " ");

  const corrections = {
    "creata": "creta", "cretaa": "creta",
    "inova": "innova", "inovva": "innova", "innnova": "innova",
    "dezire": "dzire", "desire": "dzire", "swift dzire": "dzire",
    // Ertiga, as customers have typed it ("eirtag" found nothing on 10 Oct 2026 with 5 listed).
    "eirtag": "ertiga", "eirtiga": "ertiga", "ertig": "ertiga", "ertga": "ertiga", "eritga": "ertiga",
    "ertica": "ertiga", "ertika": "ertiga", "irtiga": "ertiga", "artiga": "ertiga", "ertiaga": "ertiga",
    "diesle": "diesel", "disel": "diesel", "deisel": "diesel", "deisle": "diesel",
    "petrel": "petrol", "patrol": "petrol",
    "experiance": "experience", "experince": "experience", "expr": "experience",
    "availble": "available", "avalable": "available", "avaiable": "available", "avail": "available",
    "seden": "sedan", "sedon": "sedan",
    "hetchback": "hatchback", "hatchbak": "hatchback", "hatch": "hatchback",
    "seatar": "seater", "seter": "seater", "seatr": "seater",
    "yung": "young",
    "toyto": "toyota", "toyta": "toyota",
    "maruthi": "maruti",
    "hyndai": "hyundai", "hyundi": "hyundai", "hyndia": "hyundai",
    "telgu": "telugu", "telugi": "telugu"
  };

  Object.entries(corrections).forEach(([wrong, right]) => {
    normalizedSearch = normalizedSearch.replace(new RegExp(`\\b${wrong}\\b`, 'g'), right);
  });

  // A city typed in another script is that city in English - "ఏలూరు" is "eluru" - so it
  // reaches the answer the English word does, instead of matching no car.
  normalizedSearch = cityWordsInEnglish(normalizedSearch);

  const hasFilters = !!(filterDriverAge || filterCarType || filterSeats || filterYear || filterFuelType || filterExperience || filterRating || filterDriverTags || filterBudgetRange || filterMusicPreference || filterLanguage);
  const hasSearch = normalizedSearch.trim().length > 0;

  const { visibleCars, matchReasons, filterOnly, bestScore, explicitSort } = useMemo(() => {
    let filtered = cars;
    let matchReasonsMap = {};
    let bestScore = null;

    // Text search: Fuse.js
    if (hasSearch) {
      const searchIndex = filtered.map(car => ({
        carId: car.carId,
        carModel: car.carDetails?.model || car.carModel || car.carType || "",
        carBrand: car.carDetails?.brand || car.make || "",
        category: car.category || "",
        categoryLabel: detectCarCategory(car.carDetails?.model || car.carModel || car.carType, car.carDetails?.seats || car.seats).label,
        fuelType: car.carDetails?.fuelType || car.fuelType || "",
        transmission: car.carDetails?.transmission || "",
        seaterCount: String(car.carDetails?.seats || car.seatCount || ""),
        driverName: car.driverName || car.ownerDriver?.name || car.ownerName || "",
        rating: car.avgRating || car.rating,
        ratingTags: car.ratingTags || [],
        tripTypes: car.tripTypes || [],
        /* languagesSpoken is the field the cars carry ("Telugu, Hindi, English"). This
           read car.languages, which no car has, so every car indexed as Telugu-only and
           a search for "Hindi" found nobody. */
        languages: String(car.ownerDriver?.languagesSpoken || car.languagesSpoken || "")
          .split(",").map((x) => x.trim()).filter(Boolean),
        year: String(car.carDetails?.year || car.manufactureYear || car.makeYear || ""),
        color: car.carDetails?.color || ""
      }));

      const fuse = new Fuse(searchIndex, {
        threshold: 0.4,
        includeScore: true,
        includeMatches: true,
        minMatchCharLength: 2,
        keys: [
          { name: 'carModel', weight: 0.25 },
          { name: 'carBrand', weight: 0.15 },
          { name: 'driverName', weight: 0.15 },
          { name: 'category', weight: 0.10 },
          { name: 'categoryLabel', weight: 0.10 },
          { name: 'ratingTags', weight: 0.10 },
          { name: 'tripTypes', weight: 0.08 },
          { name: 'languages', weight: 0.07 },
          { name: 'seaterCount', weight: 0.10 },
          { name: 'fuelType', weight: 0.05 }
        ]
      });

      const results = fuse.search(normalizedSearch);
      bestScore = results.length ? Math.min(...results.map((r) => r.score ?? 1)) : null;
            
      const matchedCarIds = new Set();
      results.forEach(res => {
        matchedCarIds.add(res.item.carId);
        if (res.matches && res.matches.length > 0) {
          const topMatch = res.matches[0];
          matchReasonsMap[res.item.carId] = MATCH_WORDS[topMatch.key] || "hm_mDetails";
        }
      });
      filtered = filtered.filter(car => matchedCarIds.has(car.carId));
    }

    // One filter pass, used twice: on the text matches, and - for a search that turns
    // out to be a place - on every car, so the place view honours the same filters.
    const applyFilters = (list) => {
      let f = list;
      if (carFilter) f = f.filter(carFilter);
      if (filterDriverAge) f = f.filter(c => matchesDriverAge(c, filterDriverAge));
      if (filterCarType)   f = f.filter(c => matchesCarType(c, filterCarType));
      if (filterSeats)     f = f.filter(c => matchesSeats(c, filterSeats));
      if (filterYear)      f = f.filter(c => matchesYear(c, filterYear));
      if (filterFuelType)  f = f.filter(c => matchesFuelType(c, filterFuelType));
      if (filterExperience) f = f.filter(c => matchesExperience(c, filterExperience));
      if (filterRating)    f = f.filter(c => matchesRating(c, filterRating));
      if (filterDriverTags)     f = f.filter(c => matchesDriverTags(c, filterDriverTags));
      if (filterBudgetRange)    f = f.filter(c => matchesBudgetRange(c, filterBudgetRange, { hours, distanceKm, tripType, cityPricing, calculateFare, startAt: fromDateTime }));
      if (filterMusicPreference) f = f.filter(c => matchesMusicPreference(c, filterMusicPreference));
      if (filterLanguage)       f = f.filter(c => matchesLanguage(c, filterLanguage));
      return f;
    };
    filtered = applyFilters(filtered);
    const filterOnly = hasSearch ? applyFilters(cars) : null;

    const tripAt = fromDateTime ? new Date(fromDateTime) : new Date();
    let sorted = [...filtered].sort((a, b) => {
      // 1. Available at the trip's pickup time.
      const avA = availabilityTier(a, tripAt);
      const avB = availabilityTier(b, tripAt);
      if (avA !== avB) return avA - avB;

      // 3. The server's blended score (2, distance, is applied once drive times arrive).
      const rA = Number(a.rankingScore) || 0;
      const rB = Number(b.rankingScore) || 0;
      if (rA !== rB) return rB - rA;

      // 4. Oldest listing first.
      const timeA = new Date(a.createdAt || 0).getTime();
      const timeB = new Date(b.createdAt || 0).getTime();
      return timeA - timeB;
    });

    // An explicit sort replaces the ranking order above rather than tweaking it. The
    // default stays RECOMMENDED so the ranking engine remains the source of truth for
    // "best" and nothing changes for anyone who does not pick a sort.
    // A function, because the place and city views below have their own lists and a
    // sort the customer chose has to apply to whichever list is on screen - it used to
    // be skipped for "cars near Gollapudi", so "Price: high to low" did nothing there.
    const explicitSort = (list) => {
      if (!sortBy || sortBy === "RECOMMENDED" || sortBy === "ETA") return list;
      const fareOf = (car) => {
        if (!(distanceKm > 0) || !calculateFare) return null;
        const f = calculateFare(car, hours, distanceKm, tripType, cityPricing, { startAt: fromDateTime });
        return f && Number.isFinite(f.final) ? f.final : null;
      };
      const ratingOf = (c) => Number(c.avgRating ?? c.averageRating ?? c.rating ?? 0) || 0;
      const yearOf = (c) => Number(c.carDetails?.year || c.carDetails?.manufacturingYear || c.makeYear || 0) || 0;

      // Cars that cannot answer the sort question — no priceable fare, no rating, no
      // year — go last rather than being treated as zero, which would otherwise put
      // every unpriced car at the top of "price: low to high".
      const byNullsLast = (get, dir) => (a, b) => {
        const va = get(a), vb = get(b);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        return dir === "asc" ? va - vb : vb - va;
      };

      const comparators = {
        PRICE_LOW:  byNullsLast(fareOf, "asc"),
        PRICE_HIGH: byNullsLast(fareOf, "desc"),
        RATING:     byNullsLast((c) => (ratingOf(c) > 0 ? ratingOf(c) : null), "desc"),
        NEWEST_CAR: byNullsLast((c) => (yearOf(c) > 0 ? yearOf(c) : null), "desc"),
      };
      const cmp = comparators[sortBy];
      return cmp ? [...list].sort(cmp) : list;
    };
    sorted = explicitSort(sorted);

    return { visibleCars: sorted, matchReasons: matchReasonsMap, filterOnly, bestScore, explicitSort };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cars, normalizedSearch, carFilter, filterDriverAge, filterCarType, filterSeats, filterYear, filterFuelType, filterExperience, filterRating, filterDriverTags, filterBudgetRange, filterMusicPreference, filterLanguage, hours, distanceKm, tripType, cityPricing, calculateFare, sortBy, fromDateTime]);

  /* ── A search that is a place ─────────────────────────────────────────────────
     "Gollapudi" matches no car - it is an area. When the words find nothing (or only
     a coincidence of letters), the place words are looked up on the map near the
     cars we list; if that is a real place in reach, the list becomes every car,
     nearest to it first. See utils/placeSearch.js for when an answer is believed. */
  const otherCity = CITY_LIST_AP.find((c) => normalizedSearch.includes(c.toLowerCase()) && c.toLowerCase() !== (city || "").toLowerCase());
  const placeQuery = hasSearch ? placeWordsOf(normalizedSearch) : "";

  /* ── A search that is another city ────────────────────────────────────────────
     "eluru" from Vijayawada. It never reached the city prompt: that prompt only shows
     when nothing matches, and "eluru" always matched something - Fuse finds it close
     enough to "Telugu", which every driver speaks, so all 33 cars came back marked
     "Matched: Language" and the search looked ignored.
     When the words are just a city we serve (plus words like "cabs" or "to"), the
     customer is asking about that city: most likely a trip there. So the list is every
     car here - they are who would drive it - under a banner that offers to make the
     city the drop, or to look at cars based in that city instead. */
  const cityView = !!otherCity && placeQuery === otherCity.toLowerCase() && cars.length > 0 && !!filterOnly?.length;
  const textIsWeak = hasSearch && (visibleCars.length === 0 || (bestScore != null && bestScore > WEAK_TEXT_SCORE));
  const wantsPlace = textIsWeak && placeQuery.length >= 3 && cars.length > 0 && !otherCity &&
    !APP_WORDS.some((w) => normalizedSearch.includes(w));

  const [areaLookup, setAreaLookup] = useState({ q: "", status: "idle", place: null });
  const [cityDropBusy, setCityDropBusy] = useState(false);
  useEffect(() => {
    if (!wantsPlace) { setAreaLookup((s) => (s.status === "idle" ? s : { q: "", status: "idle", place: null })); return undefined; }
    let alive = true;
    setAreaLookup({ q: placeQuery, status: "pending", place: null });
    // Waits for typing to pause: one lookup for "gollapudi", not one per letter.
    const timer = setTimeout(() => {
      findPlaceNear(placeQuery, { city, near: supplyCentre(cars) }).then((place) => {
        if (alive) setAreaLookup({ q: placeQuery, status: place ? "found" : "none", place });
      });
    }, 600);
    return () => { alive = false; clearTimeout(timer); };
  }, [wantsPlace, placeQuery, city, cars]);

  const areaPending = wantsPlace && (areaLookup.q !== placeQuery || areaLookup.status === "pending");
  const areaPlace = wantsPlace && areaLookup.q === placeQuery && areaLookup.status === "found" ? areaLookup.place : null;

  const areaView = useMemo(() => {
    if (!areaPlace || !filterOnly?.length) return null;
    const kmByCar = {};
    filterOnly.forEach((c) => {
      const lat = Number(c.addressLat ?? c.ownerDriver?.addressLat);
      const lng = Number(c.addressLng ?? c.ownerDriver?.addressLng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) kmByCar[c.carId] = kmBetween(areaPlace, { lat, lng });
    });
    // Nearest first; a car with no coordinates cannot be placed and goes last.
    const list = [...filterOnly].sort((a, b) => (kmByCar[a.carId] ?? Infinity) - (kmByCar[b.carId] ?? Infinity));
    return { list, kmByCar };
  }, [areaPlace, filterOnly]);

  const areaActive = !!areaView;
  // The list every part below shows: the cars near a place, every car for a city
  // search, or the text matches.
  const listCars = areaActive ? areaView.list : cityView ? explicitSort(filterOnly) : visibleCars;

  /* The search history: one entry per settled search, with what it found. Held back
     while the list or the place lookup is still working, so it records the answer
     the customer saw rather than the one flashed on the way there. */
  useEffect(() => {
    if (!hasSearch || loading || areaPending) { cancelSearchLog("CARS"); return; }
    logSearch("CARS", {
      query: searchQuery,
      results: listCars.length,
      mode: areaActive || cityView ? "AREA" : (listCars.length ? "TEXT" : "NONE"),
      place: areaActive ? areaPlace.name : cityView ? otherCity : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, hasSearch, loading, areaPending, areaActive, cityView, listCars.length]);

  /* Put words into the search bar as if typed. The bar keeps its own text, so the
     suggestions and the place view set it the way a keystroke would. */
  const setSearchText = (text) => {
    const input = document.getElementById('cab-search-bar') || document.querySelector('input[placeholder*="Search"]');
    if (!input) return;
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
    nativeInputValueSetter.call(input, text);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  /* ── How far each car is from the pickup ──────────────────────────────────────
     One batched Distance Matrix call for the whole list rather than one per card:
     eleven cars would otherwise mean eleven round trips and eleven billed Google
     elements on every search. Origins are the owner's stored address — coordinates
     when present, the address text otherwise, which Distance Matrix geocodes.

     Only real answers are kept. A car whose origin cannot be resolved simply shows
     no ETA; nothing here ever falls back to the old city-centre estimate. */
  const [etaByCar, setEtaByCar] = useState({});
  const pickupKey =
    fromLocation?.location?.lat != null && fromLocation?.location?.lng != null
      ? `${fromLocation.location.lat},${fromLocation.location.lng}`
      : (typeof fromLocation === "string" ? fromLocation : (fromLocation?.value || ""));

  useEffect(() => {
    if (noEta || !pickupKey || listCars.length === 0) { setEtaByCar({}); return; }

    const entries = listCars.map((c) => {
      const lat = c.addressLat ?? c.ownerDriver?.addressLat;
      const lng = c.addressLng ?? c.ownerDriver?.addressLng;
      const origin = (lat != null && lng != null)
        ? `${lat},${lng}`
        : (c.address || c.ownerDriver?.address || "");
      return { carId: c.carId, origin };
    }).filter((e) => e.origin);

    if (entries.length === 0) { setEtaByCar({}); return; }

    let active = true;
    apiRequest("/distance/batch", {
      method: "POST",
      body: JSON.stringify({ origins: entries.map((e) => e.origin), to: pickupKey }),
    })
      .then((r) => {
        if (!active) return;
        const out = {};
        /* Zero is a real answer: the car is parked at the pickup. It was dropped as
           "no time", and once distance led the ranking that sent the three closest
           cars in Vijayawada to the bottom of the list. Null is the only "unknown";
           zero shows as a minute, which is the honest round-up. */
        (r?.durations || []).forEach((mins, i) => {
          if (mins != null && mins >= 0 && entries[i]) out[entries[i].carId] = Math.max(1, mins);
        });
        setEtaByCar(out);
      })
      // A convenience, not a blocker — the list is still usable without it.
      .catch(() => { if (active) setEtaByCar({}); });

    return () => { active = false; };
    // Keyed on the ids so a re-sort of the same cars does not re-bill the lookup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupKey, listCars.map((c) => c.carId).join(",")]);

  /* Sorting by arrival time has to happen out here rather than in the filter memo
     above, because the times themselves arrive after that memo runs. Reordering a
     derived copy also keeps visibleCars stable, which matters: the effect above is
     keyed on its car ids in order, so sorting the list it reads would re-fire the
     lookup on every result and bill Google in a loop.

     Cars whose origin Google could not resolve have no time and sort last — the same
     rule the other sorts use, rather than treating "unknown" as "nearest". */
  const displayCars = useMemo(() => {
    // A place search: Recommended means nearest to the place the customer named; any
    // other sort they pick applies as it does everywhere else.
    if (areaActive) {
      if (sortBy === "ETA") return [...listCars].sort((a, b) => (etaByCar[a.carId] ?? Infinity) - (etaByCar[b.carId] ?? Infinity));
      return (!sortBy || sortBy === "RECOMMENDED") ? listCars : explicitSort(listCars);
    }
    const base = cityView ? listCars : visibleCars;
    /* Recommended: distance band first, and nothing else changes. The sort is stable,
       so inside a band the server's ranking order from the memo above is kept exactly.
       Availability still outranks distance - a car ten minutes away that is off duty
       at pickup time is no use. Cars with no drive time go after every band. With no
       pickup set there are no times at all, and the list is the ranking as it came. */
    if (!sortBy || sortBy === "RECOMMENDED") {
      if (!base.some((c) => etaByCar[c.carId] > 0)) return base;
      const tripAt = fromDateTime ? new Date(fromDateTime) : new Date();
      return [...base].sort((a, b) => {
        const tA = availabilityTier(a, tripAt), tB = availabilityTier(b, tripAt);
        if (tA !== tB) return tA - tB;
        const bA = distanceBand(etaByCar[a.carId], distanceKm) ?? 99;
        const bB = distanceBand(etaByCar[b.carId], distanceKm) ?? 99;
        // Inside a band (cars about as near), a recommended car first (mock Rec_1).
        return bA - bB || (Date.parse(b.recommendedUntil || 0) > Date.now()) - (Date.parse(a.recommendedUntil || 0) > Date.now());
      });
    }
    if (sortBy !== "ETA") return base;
    return [...base].sort((a, b) => {
      const va = etaByCar[a.carId] ?? null;
      const vb = etaByCar[b.carId] ?? null;
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return va - vb;
    });
  }, [visibleCars, listCars, areaActive, cityView, explicitSort, etaByCar, sortBy, fromDateTime, distanceKm]);

  /* ── Call the owner straight from the card ───────────────────────────────────
     Same endpoint and payload the car detail page uses, so one flow governs leads,
     wallet and IVR. The fare is recomputed here for this car rather than passed in,
     because the card that was tapped is the only one whose price is being quoted. */
  const [callingCarId, setCallingCarId] = useState(null);
  // The car whose Call is waiting on "Call {name}?".
  const [confirmCar, setConfirmCar] = useState(null);
  // A trip starting within 6 hours: an immediate booking, not one driver's call (mock AppCall_3).
  const [instantCar, setInstantCar] = useState(null);
  // The one-click bar (any trip): open, and the model the customer searched for, if a car here has it.
  const [oneClick, setOneClick] = useState(false);

  // Everything that needs a trip first goes to the trip form - through the page, which
  // knows the form and what to hand it. From a car's own card the car goes too, so the
  // form - and the sign-in after it - comes back to that car, not to the list. (A
  // button's click event is not a car.)
  const goSetRoute = (car) => {
    if (onSetRoute) { onSetRoute(car?.carId ? car : null); return; }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // What a car's page is handed: the car and the trip, so it opens with its price.
  const carState = (car) => ({
    car, tripType, distanceKm, hours,
    fromLocation, toLocation, stops,
    fromDateTime, returnDateTime,
    pickupDateTime: fromDateTime,
    dropDateTime: returnDateTime,
    bookingEnabled, cityPricing,
  });
  // Sign in, then straight back to this car - the id travels encoded, "#" and all - where
  // the call the customer tapped starts by itself (callAfterLogin, CarDetail).
  // The trip as the booking sheet and the one-click bar send it.
  const placeOf = (loc) => (typeof loc === "string" ? loc : (loc?.value || loc?.name || ""));
  const bookingTrip = (car) => ({ city: car?.city || city, from: placeOf(fromLocation), to: placeOf(toLocation), pickupAt: fromDateTime, returnAt: returnDateTime,
    tripType: ["ROUND_TRIP", "OUTSTATION", "ROUND"].includes(tripType) ? "ROUND" : "ONE_WAY" });
  // A model the customer searched for ("innova"), kept only when a car in the list has it.
  const searchedModel = (() => {
    const q = String(searchQuery || "").trim();
    if (q.length < 3 || q.length > 25) return "";
    const ql = q.toLowerCase();
    return (listCars || []).some((c) => String(c?.carDetails?.model || c?.carModel || c?.model || "").toLowerCase().includes(ql)) ? q : "";
  })();
  const loginForCar = (car) => navigate("/login", {
    state: { redirectTo: `/car/${encodeURIComponent(car.carId)}`, payload: { ...carState(car), callAfterLogin: true } },
  });

  const handleCallDriver = (car) => {
    if (callingCarId) return;

    // No trip yet: a call records a lead against a specific route and schedule, and
    // there is nothing to record without one. Send them to set it rather than
    // failing, or worse, filing a lead with empty pickup and drop.
    const routeSet = !!((fromLocation?.value || fromLocation) && (toLocation?.value || toLocation));
    if (!routeSet) { goSetRoute(car); return; }

    track("call_tapped", { type: "car", method: "card", carId: car?.carId });
    // No login first (mock AppCall_2): the sheet, then an app call; the login only if app calls are off.

    // Confirm before placing it. On a detail page the customer has already committed
    // to one car; in a list a mis-tap is one thumb-width away, and every tap files a
    // lead in the partner's record. Asked in the page's own sheet, in the reader's
    // language - the browser's confirm box was English and named the website.
    setConfirmCar(car);
  };

  const placeCall = async (car) => {
    setConfirmCar(null);
    setCallingCarId(car.carId);
    try {
      // App calls (mock AppCall_2): Sramika's number and a code; the owner's number is never shown.
      const fa = (distanceKm > 0 && calculateFare) ? calculateFare(car, hours, distanceKm, tripType, cityPricing, { startAt: fromDateTime }) : null;
      const txt = (loc) => (typeof loc === "string" ? loc : (loc?.value || loc?.name || ""));
      const dialing = await startAppCall({ kind: "car", page: "list", body: {
        ownerId: car?.ownerPhone || car?.ownerId || car?.driverPhone || "UNKNOWN", carId: car?.carId || car?.id,
        pickupLocation: txt(fromLocation), dropLocation: txt(toLocation), distanceKm: distanceKm || 0, tripType,
        pickupDateTime: fromDateTime || null, dropDateTime: returnDateTime || null, durationHours: hours || 0,
        referenceFare: fa?.original || 0, driverFare: fa?.final || 0, finalPrice: fa?.final || 0, category: car?.category || "Hatchback",
        ...readTripExtras() } });
      if (dialing === true) return;
      if (!getUserRole()) { loginForCar(car); return; }
      // Desk staff: for themselves, or for a customer (mock CallBook_1).
      const ob = await askOnBehalf();
      if (ob === ON_BEHALF_CANCELLED) return;
      const f = (distanceKm > 0 && calculateFare)
        ? calculateFare(car, hours, distanceKm, tripType, cityPricing, { startAt: fromDateTime })
        : null;
      const asText = (loc) => (typeof loc === "string" ? loc : (loc?.value || loc?.name || ""));

      // A call Sramika connects may cost money: the gate asks first, then calls again.
      const res = await callWithWalletGate(({ confirmPaid }) => apiRequest("/leads/call-driver", {
        method: "POST",
        body: JSON.stringify({
          confirmPaid,
          ...onBehalfBody(ob),
          ownerId: car?.ownerPhone || car?.ownerId || car?.driverPhone || "UNKNOWN",
          carId: car?.carId || car?.id || "UNKNOWN",
          pickupLocation: asText(fromLocation),
          dropLocation: asText(toLocation),
          distanceKm: distanceKm || 0,
          tripType,
          pickupDateTime: fromDateTime || null,
          dropDateTime: returnDateTime || null,
          durationHours: hours || 0,
          referenceFare: f?.original || 0,
          driverFare: f?.final || 0,
          finalPrice: f?.final || 0,
          category: car?.category || "Hatchback",
          // Passengers, luggage and the note from the trip form, for the partner to see.
          ...readTripExtras(),
        }),
      }));

      if (res?.success || res?.leadId) track("call_placed", { type: "car", method: "card", carId: car?.carId, leadId: res?.leadId });
      if (res?.code === "DIRECT_CALL" && res?.partnerPhone) {
        /* Mark the lead as rung before handing over to the dialler.
           The list never did this — only the detail page's second button did — so a
           customer who called from a card left a lead the partner was never asked
           about: the dashboard only puts "did this become a trip?" on leads with a
           callTappedAt or a callCount. keepalive is what carries the request across
           the navigation. */
        if (res?.leadId) {
          apiRequest(`/leads/${encodeURIComponent(res.leadId)}/call-tapped`, {
            method: "POST",
            keepalive: true,
            body: JSON.stringify({ ownerId: car?.ownerPhone || car?.ownerId || car?.driverPhone || "UNKNOWN" }),
          }).catch(() => {});
        }
        // Hand the dialler the number rather than showing it and asking them to copy.
        window.location.href = `tel:${res.partnerPhone}`;
      } else if (res?.success) {
        alert(t("hm_callConnecting"));
      } else {
        alert(t("hm_callFailed"));
      }
    } catch (err) {
      if (showBusinessLimit(err)) return; // one business call every 6 hours (mock B2BLimit_1)
      if (err?.code === WALLET_GATE_CANCELLED) {
        // The customer chose not to pay for this call - nothing to report.
      } else if (err?.status === 403) {
        alert(t("hm_cantCall"));
      } else {
        alert(t("hm_callFailed"));
      }
    } finally {
      setCallingCarId(null);
    }
  };

  // No city: say so, and open the city list from here.
  if (!city) {
    return (
      <ListMessage icon={<Icon name="mapPin" size={22} strokeWidth={2} />} title={t("hm_pickCityCars")}>
        <button type="button" onClick={openCityPicker} className={PRIMARY_BTN}>{t("hm_chooseCity")}</button>
      </ListMessage>
    );
  }

  // Loading
  if (loading) return <Loading text={fillText(t("hm_findingCars"), { city: ln(city, "city") })} />;

  // No cars in city at all
  if (cars.length === 0) {
    return (
      <EmptyStateBanner
        badge={t("hm_networkGrowing")}
        title={fillText(t("hm_noCarsIn"), { city: ln(city, "city") })}
        description={t("hm_noCarsText")}
        primaryCta={t("hm_joinPartner")}
        onPrimaryAction={() => navigate("/cab-owner/onboarding")}
        secondaryCta={t("hm_changeCity")}
        onSecondaryAction={openCityPicker}
      />
    );
  }

  // Search/filter returned no results
  if (listCars.length === 0 && (hasSearch || hasFilters)) {
    const isSupportIntent = SUPPORT_KEYS.some(term => normalizedSearch.includes(term));
    const supportDigits = String(getCompanyConfig().supportPhoneNumber || "").replace(/\D/g, "").slice(-10);

    // The placeholder number is never offered as a way to reach anyone.
    if (isSupportIntent && supportDigits.length === 10 && supportDigits !== "9999999999") {
      return (
        <ListMessage icon={<Icon name="phone" size={22} strokeWidth={2} />} title={t("hm_supTitle")} text={t("hm_supText")}>
          <a href={`tel:+91${supportDigits}`} className={PRIMARY_BTN}>
            <Icon name="phone" size={16} stroke="#FFFFFF" fill="#FFFFFF" strokeWidth={1.4} />
            {t("ms_callUs")}
          </a>
        </ListMessage>
      );
    }

    const targetCity = CITY_LIST_AP.find(c => normalizedSearch.includes(c.toLowerCase()));
    if (targetCity && targetCity.toLowerCase() !== (city || "").toLowerCase()) {
      return (
        <ListMessage icon={<Icon name="mapPin" size={22} strokeWidth={2} />}
          title={fillText(t("hm_ocTitle"), { city: ln(targetCity, "city") })}
          text={fillText(t("hm_ocText"), { current: ln(city, "city") })}>
          <button type="button" className={PRIMARY_BTN}
            onClick={() => {
              localStorage.setItem("sramika_selected_city", targetCity);
              window.location.reload();
            }}>
            {fillText(t("hm_switchTo"), { city: ln(targetCity, "city") })}
          </button>
        </ListMessage>
      );
    }

    // Still asking the map: say so, rather than a "no cars" that may be about to change.
    if (areaPending) {
      return <Loading text={fillText(t("hm_lookingFor"), { q: searchQuery, city: ln(city, "city") })} />;
    }

    /* What people type when they are looking for something other than a car. Each
       gets its own answer and the one button that goes there; the price and place
       answers open the trip form, which is where both are set. */
    const intents = [
      {
        keys: INTENT_KEYS.account, icon: "user",
        title: "hm_iAccT", text: "hm_iAccD",
        btn: bookingEnabled ? "hm_iAccB" : "hm_loginJoin",
        action: () => navigate(bookingEnabled ? "/account" : "/login"),
      },
      {
        keys: INTENT_KEYS.partner, icon: "car",
        title: "hm_iPartT", text: "hm_iPartD",
        btn: "hm_joinPartner", action: () => navigate("/cab-owner/onboarding"),
      },
      {
        keys: INTENT_KEYS.trips, icon: "calendar",
        title: "hm_iTripsT", text: "hm_iTripsD",
        btn: "hm_iTripsB", action: () => navigate("/bookings"),
      },
      {
        keys: INTENT_KEYS.price, icon: "rupee",
        title: "hm_iPriceT", text: "hm_iPriceD",
        btn: "hm_setTrip", action: goSetRoute,
      },
      {
        keys: INTENT_KEYS.route, icon: "mapPin",
        title: "hm_iRouteT", text: "hm_iRouteD",
        btn: "hm_setTrip", action: goSetRoute,
      },
    ];

    const typed = normalizedSearch;
    const intent = intents.find((i) => i.keys.some((term) => typed.includes(term)));
    if (intent) {
      return (
        <ListMessage icon={<Icon name={intent.icon} size={22} strokeWidth={2} />} title={t(intent.title)} text={t(intent.text)}>
          <button type="button" onClick={intent.action} className={PRIMARY_BTN}>{t(intent.btn)}</button>
        </ListMessage>
      );
    }

    // Nothing matched. With words typed, the words are named and a few searches that
    // do find cars are offered; with filters alone, the filters are what to undo.
    return (
      <ListMessage icon={<Icon name="search" size={22} strokeWidth={2} />}
        title={hasSearch ? fillText(t("hm_noFound"), { q: searchQuery }) : t("hm_noMatchCars")}
        text={hasSearch ? t("hm_tryThese") : null}>
        {/* A driver's name searched on the wrong tab (mock Four_1 change 8). */}
        {hasSearch && (() => {
          const n = countNamed(driversListPath(city), searchQuery, (d) => d.driverName);
          return n > 0 ? (
            <button type="button" onClick={() => navigate("/drivers", { state: { searchQuery } })}
              className="w-full min-h-[40px] px-4 rounded-xl bg-[#7BAE00] text-white text-[11px] font-extrabold active:scale-[0.98]">
              {fillText(t("sq_inDrivers"), { n })}
            </button>
          ) : null;
        })()}
        {/* The words searched stay the ones the cars are listed under; what the button
            says is in the reader's language. */}
        {hasSearch && [
          ["Innova", "Innova"],
          ["Sedan", t("hm_ctSedan")],
          ["Telugu", nativeLanguageNames("Telugu", lang)],
          ["7-seater", fillText(t("hm_nSeats"), { n: 7 })],
        ].map(([suggestion, label]) => (
          <button
            key={suggestion}
            type="button"
            onClick={() => {
              onClearFilters?.();
              setSearchText(suggestion);
            }}
            className="min-h-[40px] px-4 rounded-full bg-[#F1F7E3] text-[#3F5E00] text-[11px] font-bold active:scale-[0.97]"
          >
            {label}
          </button>
        ))}
        {hasFilters && onClearFilters && (
          <button type="button" onClick={onClearFilters} className={hasSearch ? SECONDARY_BTN : PRIMARY_BTN}>
            {t("hm_clearFilters")}
          </button>
        )}
      </ListMessage>
    );
  }

  // Fewer cars than the city has: say it is a narrowed list, and offer all of them.
  const showCount = !areaActive && !cityView && (hasSearch || hasFilters) && listCars.length < cars.length;
  const items = countWords(listCars.length, t);
  const sortNote = sortBy && sortBy !== "RECOMMENDED" && sortLabel ? ` · ${sortLabel}` : "";
  const pickupArea = shortPlace(fromLocation);

  // One card per row on a phone
  return (
    <>
      {/* The search was a place: say so, and offer to make it the pickup - which is
          what turns "near Gollapudi" into real arrival times and prices. */}
      {areaActive && (
        <ResultStrip
          icon={<Icon name="mapPin" size={16} strokeWidth={2.2} />}
          title={fillText(t("hm_resNear"), { items, place: ln(areaPlace.name, "place") })}
          sub={t("hm_sortEta")}
          action={onUsePlaceAsPickup ? () => { onUsePlaceAsPickup(areaPlace); setSearchText(""); } : null}
          actionLabel={t("hm_setPickup")}
        />
      )}
      {/* The search was another city: most likely a trip there. */}
      {cityView && (
        <ResultStrip
          icon={<Icon name="mapPin" size={16} strokeWidth={2.2} />}
          title={fillText(t("hm_goingTo"), { city: ln(otherCity, "city") })}
          sub={fillText(t("hm_canTake"), { items, city: ln(city, "city") })}
          action={onUsePlaceAsDrop && !cityDropBusy ? async () => {
            setCityDropBusy(true);
            // Where the city is, for the route; the name alone if the map is unavailable.
            const place = await findPlaceNear(otherCity, {});
            setCityDropBusy(false);
            onUsePlaceAsDrop(place || { name: otherCity, address: `${otherCity}, Andhra Pradesh, India` });
            setSearchText("");
          } : null}
          actionLabel={t("hm_setDrop")}
          link={(
            <button type="button"
              onClick={() => { localStorage.setItem("sramika_selected_city", otherCity); window.location.reload(); }}
              className="mt-1 min-h-[32px] text-[9.5px] font-bold text-[#3F5E00] underline underline-offset-2">
              {fillText(t("hm_seeBasedIn"), { city: ln(otherCity, "city") })}
            </button>
          )}
        />
      )}
      {/* One click to a confirmed booking: the car Sramika recommends, the fare range (30 Oct 2026). */}
      {routeIsSet && fromDateTime && !areaActive && !cityView && listCars.length > 0 && !STAFF_ROLES.includes(getUserRole()) && (
        <OneClickBookingBar trip={bookingTrip(null)} onOpen={() => { track("oneclick_opened", { model: !!searchedModel }); setOneClick(true); }} />
      )}
      {/* How many, and for what: the route once there is one, the pickup area, or the
          city - said once for the whole list, never on every card. */}
      {!areaActive && !cityView && listCars.length > 0 && (
        <ResultStrip
          title={(showCount
            ? fillText(t("hm_resFound"), { items })
            : routeIsSet
              ? fillText(t("hm_resRoute"), { items, route: `${ln(shortPlace(fromLocation), "place")} → ${ln(shortPlace(toLocation), "place")}` })
              : pickupArea && pickupArea !== city
                ? fillText(t("hm_resNear"), { items, place: pickupArea })
                : fillText(t("hm_resAvailInCars"), { items, city: ln(carAreaLabel || city, "place") })) + sortNote}
          action={showCount ? () => { onClearFilters?.(); setSearchText(""); } : null}
          actionLabel={t("hm_showAll")}
          share={onShare}
          shareLabel={t("hm_shareList")}
        />
      )}
      {/* One card per row on a phone. Two-up gave each card ~200px, which is why the
          feature badge, fuel, rating, price basis and ETA all had nowhere to go and
          the photo had to carry overlays. The web app keeps a multi-column grid from
          the small breakpoint up, so nothing regresses on a desktop. */}
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
        {displayCars.map((car, cardIndex) => {
          const isRouteSet = routeIsSet;
          return (
          <Fragment key={car.carId}>
          <CarCard
            car={car}
            eager={cardIndex < EAGER_CARDS}
            distanceKm={distanceKm}
            hours={hours}
            startAt={fromDateTime}
            tripType={tripType}
            calculateFare={calculateFare}
            cityPricing={cityPricing}
            matchReason={areaActive || cityView || !matchReasons[car.carId] ? undefined : t(matchReasons[car.carId])}
            hasRoute={isRouteSet}
            etaMins={etaByCar[car.carId]}
            nearLabel={areaActive && areaView.kmByCar[car.carId] != null
              ? fillText(t("hm_distFrom"), { dist: kmText(areaView.kmByCar[car.carId], t), place: ln(areaPlace.name, "place") }) : null}
            onCallDriver={handleCallDriver}
            calling={callingCarId === car.carId}
            onSeePrice={() => goSetRoute(car)}
            onClick={() => {
              // No trip yet: the card's own tap goes where the price comes from.
              if (!isRouteSet) { goSetRoute(car); return; }
              // Require login before entering car detail - and come back to this car.
              if (!bookingEnabled && onLoginRequired) {
                onLoginRequired(car, carState(car));
                return;
              }
              navigate(`/car/${car.carId}`, { state: carState(car) });
            }}
          />
          {afterThird && cardIndex === Math.min(2, displayCars.length - 1) && (
            <div className="sm:col-span-2 lg:hidden">{afterThird}</div>
          )}
          </Fragment>
        )})}
      </div>

      {/* Do you own a car? - below the listings, as the Hire Driver list asks "Are you a
          driver?" there (the owner asked for it, Oct 2026). Not shown to car owners and
          drivers, who have joined already. */}
      {!["CAB_OWNER", "OWNER", "ALTING_DRIVER"].includes(getUserRole()) && (
        <section className="mt-2 rounded-2xl bg-white border border-[#E5E7EB] p-3 flex items-center gap-3 shadow-[0_2px_10px_rgba(15,23,42,0.04)]">
          <span className="w-[34px] h-[34px] rounded-[10px] bg-[#F1F7E3] text-[#5E8700] flex items-center justify-center shrink-0">
            <Icon name="car" size={18} strokeWidth={2.2} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[11.5px] font-bold text-[#1F2937] leading-tight">{t("hm_joinCarT")}</p>
            <p className="text-[9.5px] text-[#64748B] leading-snug mt-0.5">{t("hm_joinCarD")}</p>
          </div>
          <button type="button" onClick={() => navigate("/cab-owner/onboarding")}
            className="shrink-0 min-h-[40px] px-4 rounded-[11px] border border-[#7BAE00] text-[#3F5E00] bg-white text-[11px] font-bold active:scale-[0.98]">
            {t("hm_join")}
          </button>
        </section>
      )}

      {(instantCar || oneClick) && (
        <InstantBookingSheet mode={instantCar ? "instant" : "any"} initialSeats={instantCar ? seatClassOf(instantCar) : ""} initialModel={instantCar ? "" : searchedModel}
          onClose={() => { setInstantCar(null); setOneClick(false); }}
          onLogin={() => (instantCar ? loginForCar(instantCar) : navigate("/login", { state: { redirectTo: window.location.pathname + window.location.search } }))}
          trip={bookingTrip(instantCar)} />
      )}
      {confirmCar && (() => {
        const who = String(confirmCar.driverName || confirmCar.ownerDriver?.name || "").trim().split(/\s+/)[0];
        const person = who ? ln(who.charAt(0).toUpperCase() + who.slice(1).toLowerCase(), "person") : t("hm_thePartner");
        const carName = ln(shortName(confirmCar.carDetails?.model || confirmCar.carModel || ""), "car");
        return (
          <CallConfirmSheet
            title={fillText(t("hm_confirmTitle"), { name: person })}
            text={carName ? fillText(t("hm_confirmText"), { car: carName }) : null}
            callLabel={t("hm_call")}
            cancelLabel={t("hm_cancel")}
            onCall={() => placeCall(confirmCar)}
            onCancel={() => setConfirmCar(null)}
          />
        );
      })()}
    </>
  );
}
